import type { Feldfehler, Schritt } from "@fbs/funnel-schema";

/**
 * Der Vertrag zwischen der Hülle und den einzelnen Fragetypen.
 *
 * Die Fragetypen wissen nichts vom Funnel als Ganzem. Sie bekommen ihren
 * Schritt, den aktuellen Wert und zwei Rückrufe, mehr nicht. Dadurch lässt
 * sich jeder Typ für sich ansehen und später im Builder als Vorschau zeigen,
 * ohne dass ein Funnel dahinterstehen muss.
 */
export interface SchrittProps<S extends Schritt = Schritt> {
  schritt: S;
  wert: unknown;
  fehler: Feldfehler[];
  /** Bei geteilten Kontaktschritten: das gerade sichtbare Feld. Sonst null. */
  teil: number | null;
  /**
   * Ist das die erste Frage des Funnels?
   *
   * Nur fürs Messen. Der Funnel hat keinen Startknopf - er beginnt damit,
   * dass jemand die erste Antwort antippt. Ohne eine eigene Kennung an diesen
   * Karten ist dieser Moment von außen nicht zu erkennen: Jede Antwortkarte
   * jeder Frage trägt dieselbe Klasse.
   *
   * Verändert nichts am Aussehen und nichts am Ablauf. Es hängt eine Klasse
   * an, an der ein Messwerkzeug den Anfang festmachen kann.
   */
  istStart: boolean;
  /**
   * Setzt die Antwort für diesen Schritt.
   *
   * Statt eines Wertes darf auch eine Funktion übergeben werden, die den
   * vorherigen Wert bekommt. Das ist nötig, wenn mehrere Felder desselben
   * Bildschirms gleichzeitig gefüllt werden können.
   */
  onWert: (wert: unknown) => void;
  /**
   * Bittet darum, weiterzugehen. Die Hülle prüft vorher; ein ungültiger
   * Bildschirm bleibt stehen.
   */
  onWeiter: () => void;
  /**
   * Wie onWeiter, aber nach der eingestellten kurzen Verzögerung. Für das
   * Weitergehen von selbst nach einer Einfachauswahl.
   */
  onAutoWeiter: () => void;
}

type Kontaktschritt = Extract<Schritt, { type: "contact" }>;
export type Kontaktfeld = Kontaktschritt["fields"][number];

/**
 * Was der Browser über ein Kontaktfeld wissen muss.
 *
 * Das richtige `autocomplete` ist hier kein Detail: Wer seine Daten im Browser
 * gespeichert hat, füllt den ganzen Schritt mit einem Tipp aus. Genau an
 * dieser Stelle springen sonst die meisten ab.
 *
 * Steht hier und nicht in der Kontaktkomponente, weil auch die Hülle die
 * Beschriftung braucht: Bei getrennten Bildschirmen ist sie die Überschrift.
 */
export const KONTAKT_FELDER: Record<
  Kontaktfeld["name"],
  { label: string; typ: string; autocomplete: string; inputmode?: string }
> = {
  first_name: { label: "Vorname", typ: "text", autocomplete: "given-name" },
  last_name: { label: "Nachname", typ: "text", autocomplete: "family-name" },
  phone: { label: "Telefonnummer", typ: "tel", autocomplete: "tel", inputmode: "tel" },
  email: {
    label: "E-Mail-Adresse",
    typ: "email",
    autocomplete: "email",
    inputmode: "email",
  },
  street: { label: "Straße und Hausnummer", typ: "text", autocomplete: "street-address" },
  company: { label: "Firma", typ: "text", autocomplete: "organization" },
};

export function kontaktBeschriftung(feld: Kontaktfeld): string {
  return feld.label ?? KONTAKT_FELDER[feld.name].label;
}

export function fehlerText(fehler: Feldfehler[], feld?: string): string | null {
  const treffer = feld
    ? fehler.find((f) => f.field === feld)
    : fehler.find((f) => !f.field);
  return treffer?.message ?? null;
}

/** Liest einen Wert als Zeichenkette, ohne bei undefined zu stolpern. */
export function alsText(wert: unknown): string {
  if (typeof wert === "string") return wert;
  if (typeof wert === "number") return String(wert);
  return "";
}

export function alsListe(wert: unknown): string[] {
  return Array.isArray(wert)
    ? wert.filter((w): w is string => typeof w === "string")
    : [];
}

/**
 * Liest einen Wert als Zahl und sagt dazu, ob überhaupt einer da ist.
 *
 * Die Trennung ist der ganze Zweck dieser Funktion. `Number("")` ergibt 0, und
 * 0 ist eine endliche Zahl: Wer nur auf `Number.isFinite` prüft, zeigt einem
 * Nutzer, der noch nichts angetippt hat, eine 0 an. Bei einer Frage mit
 * Mindestwert 1 steht dann eine Antwort auf dem Bildschirm, die er nie gegeben
 * hat und die die Prüfung nicht bestehen würde.
 */
export function alsZahl(wert: unknown): { hatWert: boolean; zahl: number } {
  const roh = typeof wert === "number" ? wert : alsText(wert).trim();
  const hatWert = roh !== "" && Number.isFinite(Number(roh));
  return { hatWert, zahl: hatWert ? Number(roh) : Number.NaN };
}

export function alsObjekt(wert: unknown): Record<string, unknown> {
  return typeof wert === "object" && wert !== null && !Array.isArray(wert)
    ? (wert as Record<string, unknown>)
    : {};
}
